import { nanoid } from 'nanoid'
import {
  CustomPeriodFilter,
  DatasetDataValue,
  DatasetType,
  ParsedSegmentPath,
  PeriodFilter
} from 'types/GlobalWidget'
import { Filters } from './types'
import type {
  TAttributeDataType,
  TAttributeOptionDto
} from '@homepal/api-contract'

import {
  Condition,
  FilterConditionFormatBody,
  FilterType
} from 'types/GlobalKpiOption'
import { TMetricQueryParams } from 'types/GlobalMetric'

import { Customer, PeriodType } from 'types/GlobalCustomer'
import {
  TDashboardFilterOption,
  ParsedDashboardFilterOptions
} from 'types/GlobalDashboardFilter'

// TODO: fix, utils functions ska ej importera från en komponent
import { Option } from 'components_new/molecules/SelectSearch'
import { GroupMember, TGroupBase, MemberStatus } from 'types/GlobalGroup'

import { Strings, TFunction } from 'language/translator'
import {
  TPostInviteNewUserToDashboardGroup,
  TPostInviteToDashboardGroupPermission
} from 'redux/api/DashboardGroups/types'

const THUMBNAIL_BASE_URL =
  'https://homepal-dashboard-thumbnails.s3.eu-north-1.amazonaws.com'

export const DASHBOARD_THUMBNAIL_PLACEHOLDER =
  '/assets/media/dashboard_thumbnail.svg'

/**
 * Get thumbnail for dashboard.
 * Adding reload query parameter to avoid caching.
 * @param thumbnail
 * @returns
 */
export const getThumbnail = (thumbnail: string | null) =>
  thumbnail
    ? `${thumbnail}?reload=${nanoid()}`
    : DASHBOARD_THUMBNAIL_PLACEHOLDER

export const getDashboardImageUrl = (id: string) => {
  return `${THUMBNAIL_BASE_URL}/production/${id}.png`
}

// Type guard function for PeriodFilter
export function isPeriodFilterEnum(
  value: PeriodFilter | CustomPeriodFilter
): value is PeriodFilter {
  return typeof value === 'string' && value in PeriodFilter
}

// Type guard function for CustomPeriodFilter
export function isCustomPeriodFilter(
  value: PeriodFilter | CustomPeriodFilter
): value is CustomPeriodFilter {
  return typeof value === 'object' && 'from' in value
}

// Resolve a period filter (a segment override or the baseline) into the flat
// period_filter / from_date / to_date fields used by the metric query params
// and the chat metric view.
export function resolvePeriodFields(
  periodFilterToUse: PeriodFilter | CustomPeriodFilter | null
): {
  period_filter: PeriodFilter | null
  from_date: string | null
  to_date: string | null
} {
  if (
    periodFilterToUse &&
    isCustomPeriodFilter(periodFilterToUse) &&
    periodFilterToUse.from &&
    periodFilterToUse.to
  ) {
    return {
      period_filter: null,
      from_date: periodFilterToUse.from,
      to_date: periodFilterToUse.to
    }
  }

  if (periodFilterToUse && isPeriodFilterEnum(periodFilterToUse)) {
    return {
      period_filter: periodFilterToUse,
      from_date: null,
      to_date: null
    }
  }

  return { period_filter: null, from_date: null, to_date: null }
}

export function getFilterFromSegmentPaths(
  segmentPaths: ParsedSegmentPath[]
): Filters {
  const filters: Filters = {}

  segmentPaths.forEach((item) => {
    if (!item.attribute || item.period) {
      return
    }

    if (item.filter_attribute in filters) {
      const filter = filters[item.filter_attribute]
      const values = filter.value as (string | number | null)[]
      const displayValues = filter.display_value as (string | number | null)[]

      values.push(item.filter_value)
      displayValues.push(item.display_label)
    } else {
      filters[item.filter_attribute] = {
        attributeId: item.filter_attribute,
        condition: Condition.EQ,
        value: [item.filter_value],
        // Parallel display label(s), e.g. the agent's name for a
        // handläggare segment whose filter_value is a raw id. Uses the
        // parsed/localized label (same as the dialog title) so dates,
        // date-parts and null values render formatted, not raw.
        display_value: [item.display_label],
        type: FilterType.INPUT_VALUE
      }
    }
  })

  return filters
}

/**
 * Converts a KPI option's own persisted filter conditions into the
 * `TMetricQueryParams.filter` shape used to fetch metric raw data. Only
 * equality conditions (single value or multi-select "one of") can be
 * represented this way — ranges (FROM_TO) and null-checks (IS_NULL /
 * IS_NOT_NULL) have no equivalent in the `{ eq: string[] }` shape and are
 * skipped.
 */
export function kpiFiltersToQueryFilter(
  filters: FilterConditionFormatBody[],
  attributeOptionsById: { [id: string]: TAttributeOptionDto }
): NonNullable<TMetricQueryParams['filter']> {
  const filter: NonNullable<TMetricQueryParams['filter']> = {}

  filters.forEach((item) => {
    if (item.condition.selected !== Condition.EQ) {
      return
    }

    const attributeOptionId = item.attribute_option.selected
    const attributeOption = attributeOptionId
      ? attributeOptionsById[attributeOptionId]
      : null

    if (!attributeOption) {
      return
    }

    const selected = item.value.selected
    const vals = (Array.isArray(selected) ? selected : [selected])
      .filter((value): value is string | number => value !== null)
      .map(String)

    if (vals.length === 0) {
      return
    }

    const attributeId = attributeOption.attribute_id

    filter[attributeId] = {
      eq: [...(filter[attributeId]?.eq ?? []), ...vals]
    }
  })

  return filter
}

/**
 * Parses array to object which uses the id as key.
 */
export function arrayToObject<T>(arr: (T & { id: string })[]) {
  const obj: { [id: string]: T } = {}

  arr.forEach((item) => {
    obj[item.id] = item
  })

  return obj
}

/**
 * Groups array to object of arrays by key.
 */
export function groupBy<T>(arr: (T & { [key: string]: any })[], key: string) {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    if (!acc[item[key]]) {
      acc[item[key]] = []
    }

    acc[item[key]].push(item)

    return acc
  }, {})
}

export function getDashboardLimitReached(
  isHomepal: boolean,
  customer: Customer | null
) {
  if (isHomepal) {
    return false
  }

  const numberOfDashboardsInLicense = customer?.number_of_dashboards || 0
  const numberOfDashboardsInUse = customer?.number_of_dashboards_in_use || 0

  return numberOfDashboardsInLicense
    ? numberOfDashboardsInUse >= numberOfDashboardsInLicense
    : false
}

/**
 * Check if the customer is missing a usable "latest closed
 * accounting period" setting, i.e. no period type is set or
 * it is set to MANUALLY without a closed period.
 */
export function isMissingPeriodSetting(customer: Customer | null) {
  if (!customer) {
    return false
  }

  return (
    !customer.period_type ||
    (customer.period_type === PeriodType.MANUALLY &&
      !customer.latest_closed_period)
  )
}

/**
 * Retrieve value from data based on DatasetType.
 * - Special parsing for NUMBER_TAG
 */
export function parseDatasetValue(
  dataValue: DatasetDataValue,
  datasetType: DatasetType = DatasetType.STRING
) {
  if (datasetType === DatasetType.NUMBER_TAG) {
    // pick the first one because response should only
    // return at most one item in the array.
    return (dataValue as number[])[0] ?? null
  }

  return dataValue
}

/**
 * Check if Dataset is of type number (single number/list of numbers).
 */
export function isNumberDatasetType(dtype?: DatasetType) {
  return dtype && [DatasetType.NUMBER, DatasetType.NUMBER_TAG].includes(dtype)
}

export function formatResourceFilterOptions(
  resourceFilters: {
    attribute_id: string
    index: number
  }[],
  resourceFilterOptions: TDashboardFilterOption[],
  resourceFilterData: { [attributeId: string]: string[] | undefined },
  saveKey: string | null
): Map<string, ParsedDashboardFilterOptions> {
  return new Map(
    resourceFilters.map((filter) => {
      const option = resourceFilterOptions.find(
        (opt) => opt.attribute_id === filter.attribute_id
      )

      return [
        filter.attribute_id,
        {
          attribute_id: filter.attribute_id,
          multiselect: option?.multiselect ?? true,
          title: option?.title || '',
          index: filter.index,
          options: resourceFilterData[`${filter.attribute_id}${saveKey || ''}`]
        }
      ]
    })
  )
}

// typeguard for new user invite via dashboard
export const isNewUserInvite = (
  permission: TPostInviteToDashboardGroupPermission
): permission is TPostInviteNewUserToDashboardGroup =>
  'new_user_email' in permission

export const formatUsername = (user: {
  first_name: string | null
  last_name: string | null
}) => {
  return user.first_name || user.last_name
    ? `${user.first_name || ''} ${user.last_name || ''}`
    : 'Namn saknas'
}

export const formatUserOption = (
  user: {
    id: string
    email: string
    first_name: string | null
    last_name: string | null
  } | null
): Option & { id: string } => {
  if (!user) {
    return {
      avatar: 'user',
      body: '',
      id: '',
      title: ''
    }
  }

  return {
    avatar: 'user',
    body: user.email,
    id: user.id,
    title: formatUsername(user)
  }
}

export const formatGroupOption = (
  group: TGroupBase,
  t: (namespace: 'global', key: Strings<'global'>, lower?: boolean) => string
): Option & { id: string } => {
  return {
    avatar: 'group',
    body: `${group.members.length} ${t(
      'global',
      group.members.length === 1 ? 'member' : 'members',
      true
    )}`,
    color: group.profile_color,
    id: group.id,
    title: group.name
  }
}
// Validate if account has a pending request
export const userIsPending = (members: GroupMember[], accountId: string) =>
  members.some(
    (member) =>
      member.status === MemberStatus.PENDING && member.account_id === accountId
  )

/**
 * Check if filter condition is of type where
 * there is no compared value.
 * @param condition - Filter condition.
 * @returns - is NULL/NOT NULL
 */
export const isOnlyLeftFilter = (condition: Condition) =>
  [Condition.IS_NULL, Condition.IS_NOT_NULL].includes(condition)

export type TNumericFilterCondition =
  | Condition.EQ
  | Condition.LT
  | Condition.GT
  | Condition.IS_NULL
  | Condition.IS_NOT_NULL

/**
 * Filter conditions available for numeric attributes.
 */
export const NUMERIC_FILTER_CONDITIONS: TNumericFilterCondition[] = [
  Condition.EQ,
  Condition.LT,
  Condition.GT,
  Condition.IS_NULL,
  Condition.IS_NOT_NULL
]

/**
 * Check if filter condition is available for numeric attributes.
 * @param condition - Filter condition.
 * @returns - is a numeric condition
 */
export const isNumericCondition = (condition: Condition) =>
  (NUMERIC_FILTER_CONDITIONS as Condition[]).includes(condition)

/**
 * Check if attribute data type is filtered with numeric input.
 * @param type - Attribute data type.
 * @returns - is BIGINT/DOUBLE
 */
export const isNumericFilter = (type?: TAttributeDataType) =>
  type !== undefined && ['BIGINT', 'DOUBLE'].includes(type)

/**
 * Check if the platform is Apple or not.
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false // SSR

  // Prefer UA-CH (userAgentData) when available
  const uaPlatform = (navigator as any).userAgentData?.platform ?? ''

  if (uaPlatform) {
    const p = uaPlatform.toLowerCase()

    if (p.includes('mac') || p.includes('ios')) return true

    return false
  }

  // Fallbacks
  const plat = (navigator.platform || '').toLowerCase()
  const ua = (navigator.userAgent || '').toLowerCase()

  // iPadOS 13+ reports "MacIntel" → detect via touch points
  const isIPadOS = plat === 'macintel' && (navigator as any).maxTouchPoints > 1

  return (
    plat.includes('mac') ||
    plat.includes('iphone') ||
    plat.includes('ipad') ||
    plat.includes('ipod') ||
    isIPadOS ||
    ua.includes('mac os x') || // extra safety
    ua.includes('iphone') ||
    ua.includes('ipad')
  )
}

/**
 * Joins an array of strings with commas but uses localized 'and'
 * as the last separator.
 * @param t - t function from useTranslation()
 * @returns - string of join values.
 */
export const joinWithAnd = (params: { t: TFunction; values: string[] }) => {
  const { t, values } = params
  const and = t('global', 'and', true)

  return values.length < 3
    ? values.join(` ${and} `)
    : `${values.slice(0, -1).join(', ')} ${and} ${values.at(-1)}`
}

/**
 * Returns the option from the given list that matches the provided value.
 *
 * @param value - The identifier to match, or `null` if no selection is made.
 * @param opts - The list of option objects to search through.
 *
 * @returns The matching option object if found, otherwise `null`.
 */
export function getSelectedOption<T, O extends { id: T }>(
  value: T | null,
  opts: O[]
): O | null {
  return value ? opts.find((opt) => opt.id === value) ?? null : null
}

/**
 * Returns all options from the given list whose IDs appear in the provided array of values.
 *
 * @param values - An array of string IDs representing the selected options.
 * @param opts - The list of option objects to filter.
 *
 * @returns An array of all matching option objects.
 * Returns an empty array if no matches are found.
 */
export function getSelectedOptions<O extends { id: string }>(
  values: string[],
  opts: O[]
): O[] {
  return opts.filter((opt) => values.includes(opt.id))
}
